import Phaser from 'phaser';
import { assertGameContentValid } from './data/validate-content';
import { GameScene } from './render/scenes/game-scene';
import { AutoSaveManager } from './save/autosave';
import { loadSlot } from './save/storage';
import type { StorageLike } from './save/types';
import { Simulation } from './sim/Simulation';
import { formatGameDate, getGameDate } from './sim/time';
import { HUD } from './ui/hud';

// Storage adapter standar membungkus window.localStorage dengan proteksi try/catch
const browserStorage: StorageLike = {
  getItem: (key: string) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    window.localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    window.localStorage.removeItem(key);
  }
};

function initGame(): void {
  // 1. Validasi integritas JSON data saat boot
  assertGameContentValid();

  // 2. Buat simulasi default
  let sim = new Simulation();
  const autoSaveManager = new AutoSaveManager(browserStorage);

  // 3. Cek save yang ada untuk alur boot
  const manualLoad = loadSlot(browserStorage, 'manual');
  const autoLoad = loadSlot(browserStorage, 'autosave');

  let chosenInitialState = sim.getSnapshot();
  let initialSaveIndicatorText = 'Belum ada save';

  // Pilih save paling mutakhir jika ada
  let candidateSlot: 'manual' | 'autosave' | null = null;
  if (manualLoad.ok && autoLoad.ok) {
    candidateSlot = new Date(manualLoad.savedAt).getTime() >= new Date(autoLoad.savedAt).getTime() ? 'manual' : 'autosave';
  } else if (manualLoad.ok) {
    candidateSlot = 'manual';
  } else if (autoLoad.ok) {
    candidateSlot = 'autosave';
  }

  // Jika ada save rusak di kedua slot, matikan autosave agar tidak menimpa save rusak tersebut
  if ((!manualLoad.ok && manualLoad.reason === 'CORRUPT_JSON') || (!autoLoad.ok && autoLoad.reason === 'CORRUPT_JSON')) {
    autoSaveManager.setEnabled(false);
  }

  if (candidateSlot) {
    const candidateLoad = candidateSlot === 'manual' ? manualLoad : autoLoad;
    if (candidateLoad.ok) {
      chosenInitialState = candidateLoad.state;
      sim.loadState(chosenInitialState);
      const dateStr = formatGameDate(chosenInitialState.totalGameMs);
      initialSaveIndicatorText = `Tersimpan: ${dateStr} (${candidateSlot})`;
    }
  }

  // 4. Instansiasi HUD DOM Overlay
  const hud = new HUD(sim, browserStorage);
  hud.updateSaveIndicator(initialSaveIndicatorText);
  hud.setLastSavedGameMs(chosenInitialState.totalGameMs);

  // 5. Konfigurasi Phaser 3 Game
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent: 'game-container',
    width: 1280,
    height: 720,
    backgroundColor: '#1a1e24',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH
    },
    scene: [GameScene]
  };

  const game = new Phaser.Game(config);
  game.scene.start('GameScene', { sim, hud });

  // 6. Callback sinkronisasi State & Render
  hud.onStateLoaded(() => {
    const scene = game.scene.getScene('GameScene') as GameScene;
    if (scene) {
      scene.syncFromState();
    }
  });

  hud.onNewGame(() => {
    sim = new Simulation();
    hud.setSimulation(sim);
    autoSaveManager.setEnabled(true);
    const scene = game.scene.getScene('GameScene') as GameScene;
    if (scene) {
      scene.init({ sim, hud });
      scene.syncFromState();
    }
    hud.setLastSavedGameMs(0);
    hud.updateSaveIndicator('Game Baru (belum disimpan)');
    hud.showToast('Game baru dimulai!', 'info');
  });

  // 7. Auto-save trigger loop (deteksi pergantian hari & throttle)
  let lastCheckedGameDay = getGameDate(sim.getState().totalGameMs).day;
  setInterval(() => {
    const currentDay = getGameDate(sim.getState().totalGameMs).day;
    if (currentDay > lastCheckedGameDay) {
      const res = autoSaveManager.triggerAutoSave(sim.getSnapshot(), currentDay);
      if (res?.ok) {
        lastCheckedGameDay = currentDay;
        const dateStr = formatGameDate(sim.getState().totalGameMs);
        hud.updateSaveIndicator(`Tersimpan: ${dateStr} (otomatis)`);
        hud.showToast('Auto-save berhasil disimpan!', 'info');
      }
    }
  }, 1000);

  // 8. Auto-save trigger saat tab HP / desktop ditutup atau disembunyikan
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      const currentDay = getGameDate(sim.getState().totalGameMs).day;
      autoSaveManager.triggerAutoSave(sim.getSnapshot(), currentDay, true);
    }
  });

  window.addEventListener('pagehide', () => {
    const currentDay = getGameDate(sim.getState().totalGameMs).day;
    autoSaveManager.triggerAutoSave(sim.getSnapshot(), currentDay, true);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGame);
} else {
  initGame();
}
